import {
  Button,
  Input,
  InputMask,
  SelectSearch,
  ImageUpload,
  Form,
  ModalFooter,
  errorToast,
} from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalization } from "@i18n";
import { createClientSchema, ClientFormData } from "../../schemas";
import { useClientQuery } from "../../api/client";
import { LoadingBlurDiv } from "@shared/components";
import { AxiosError } from "axios";
import { buildAssetUrl } from "@shared/utils";
import {
  getBrazilianStates,
  getCountryOptions,
  normalizeStateToFullName,
} from "@shared/constants";

interface EditClientFormProps {
  clientId?: string;
  onSubmit: (data: ClientFormData) => void;
  onCancel: () => void;
}

export const EditClientForm = ({
  clientId,
  onSubmit,
  onCancel,
}: EditClientFormProps) => {
  const { translate } = useLocalization();

  const { data: clientData, isLoading, error, isError } = useClientQuery(clientId ?? "", {
    enabled: !!clientId,
  });

  if (isError) {
    const axiosError = error as AxiosError;
    errorToast({
      description: axiosError.parsedMessage ?? translate("common.errorEditing"),
    });
    onCancel();
  }

  const form = useForm<ClientFormData>({
    resolver: zodResolver(createClientSchema(translate)),
    values: {
      cnpj: clientData?.cnpj ?? "",
      name: clientData?.name ?? "",
      fantasyName: clientData?.fantasyName ?? "",
      phone: clientData?.phone ?? "",
      municipalRegistration: clientData?.municipalRegistration ?? "",
      logo: clientData?.logo ?? [],
      address: {
        zipCode: clientData?.address?.zipCode ?? "",
        street: clientData?.address?.street ?? "",
        complement: clientData?.address?.complement ?? "",
        neighborhood: clientData?.address?.neighborhood ?? "",
        number: clientData?.address?.number ?? 0,
        city: clientData?.address?.city ?? "",
        state: normalizeStateToFullName(clientData?.address?.state),
        country: clientData?.address?.country ?? "",
      },
      responsible: clientData?.responsible
        ? {
            name: clientData.responsible.name,
            email: clientData.responsible.email,
            phone: clientData.responsible.phone,
          }
        : {
            name: "",
            email: "",
            phone: "",
          },
    },
  });

  return (
    <LoadingBlurDiv isLoading={isLoading}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="px-[32px] py-[16px] max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-x-4">
              <div className="flex justify-between items-end col-span-2">
                <div>
                  <h3 className="text-body-medium-bold mt-2 mb-4">
                    {translate("management.clients.form.section.generalData")}
                  </h3>
                  <InputMask
                    control={form.control}
                    label={translate("management.clients.form.label.cnpj")}
                    name="cnpj"
                    type="text"
                    mask="99.999.999/9999-99"
                    placeholder={translate(
                      "management.clients.form.placeholder.cnpj",
                    )}
                    className="w-[521.5px]"
                  />
                </div>
                <div>
                  <ImageUpload
                    control={form.control}
                    label={translate("management.clients.form.label.logo")}
                    name="logo"
                    accept="image/jpeg,image/jpg,image/png"
                    preview={
                      typeof clientData?.logo === "string"
                        ? clientData.logo
                        : undefined
                    }
                    defaultImages={
                      typeof clientData?.logo === "string"
                        ? [{ src: buildAssetUrl(clientData.logo) }]
                        : (clientData?.logo as string[] | undefined)
                    }
                    allowRemove
                  />
                </div>
              </div>
              <Input
                control={form.control}
                label={translate("management.clients.form.label.company")}
                name="name"
                type="text"
                placeholder={translate(
                  "management.clients.form.placeholder.company",
                )}
              />
              <Input
                control={form.control}
                label={translate("management.clients.form.label.tradeName")}
                name="fantasyName"
                type="text"
                placeholder={translate(
                  "management.clients.form.placeholder.tradeName",
                )}
              />
              <InputMask
                control={form.control}
                label={translate(
                  "management.clients.form.label.companyPhone",
                )}
                name="phone"
                type="text"
                mask="(99) 99999-9999"
                placeholder={translate(
                  "management.clients.form.placeholder.companyPhone",
                )}
              />
              <Input
                control={form.control}
                label={translate(
                  "management.clients.form.label.municipalRegistration",
                )}
                name="municipalRegistration"
                type="text"
                placeholder={translate(
                  "management.clients.form.placeholder.municipalRegistration",
                )}
              />
            </div>

            {/* Seção: Endereço */}
            <h3 className="text-body-medium-bold mt-2 mb-4">
              {translate("management.clients.form.section.address")}
            </h3>
            <div className="flex flex-col gap-x-4">
              {/* Linha 1: CEP */}
              <div className="grid grid-cols-2 gap-x-4">
                <InputMask
                  control={form.control}
                  label={translate("management.clients.form.label.zipCode")}
                  name="address.zipCode"
                  type="text"
                  mask="99999-999"
                  placeholder={translate(
                    "management.clients.form.placeholder.zipCode",
                  )}
                  maxLength={9}
                  className="col-span-2"
                />
              </div>
              {/* Linha 2: Rua | Complemento */}
              <div className="grid grid-cols-2 gap-x-4">
                <Input
                  control={form.control}
                  label={translate("management.clients.form.label.street")}
                  name="address.street"
                  type="text"
                  placeholder={translate(
                    "management.clients.form.placeholder.street",
                  )}
                  className="col-span-8"
                />
                <Input
                  control={form.control}
                  label={translate("management.clients.form.label.complement")}
                  name="address.complement"
                  type="text"
                  placeholder={translate(
                    "management.clients.form.placeholder.complement",
                  )}
                  className="col-span-4"
                />
              </div>

              <div className="grid grid-cols-12 gap-x-4">
                <div className="col-span-4">
                  <Input
                    control={form.control}
                    label={translate("management.clients.form.label.neighborhood")}
                    name="address.neighborhood"
                    type="text"
                    placeholder={translate(
                      "management.clients.form.placeholder.neighborhood",
                    )}
                    className="w-full"
                  />
                </div>
                <Input
                  control={form.control}
                  label={translate("management.clients.form.label.number")}
                  name="address.number"
                  type="text"
                  placeholder={translate(
                    "management.clients.form.placeholder.number",
                  )}
                  className="w-full"
                />
                <div className="col-span-3">
                  <Input
                    control={form.control}
                    label={translate("management.clients.form.label.city")}
                    name="address.city"
                    type="text"
                    placeholder={translate(
                      "management.clients.form.placeholder.city",
                    )}
                    className="w-full"
                  />
                </div>
                <div className="col-span-2">
                  <SelectSearch
                    control={form.control}
                    label={translate("management.clients.form.label.state")}
                    name="address.state"
                    placeholder={translate(
                      "management.clients.form.placeholder.state",
                    )}
                    options={getBrazilianStates(translate)}
                    className="w-full"
                  />
                </div>
                <div className="col-span-2">
                  <SelectSearch
                    control={form.control}
                    label={translate("management.clients.form.label.country")}
                    name="address.country"
                    placeholder={translate(
                      "management.clients.form.placeholder.country",
                    )}
                    options={getCountryOptions(translate)}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Seção: Responsável */}
            <h3 className="text-body-medium-bold mt-2 mb-4">
              {translate("management.clients.form.section.responsible")}
            </h3>
            <div className="grid grid-cols-3 gap-x-4">
              <Input
                control={form.control}
                label={translate(
                  "management.clients.form.label.responsibleName",
                )}
                name="responsible.name"
                type="text"
                placeholder={translate(
                  "management.clients.form.placeholder.responsibleName",
                )}
                className="col-span-1"
              />
              <Input
                control={form.control}
                label={translate(
                  "management.clients.form.label.responsibleEmail",
                )}
                name="responsible.email"
                type="email"
                placeholder={translate(
                  "management.clients.form.placeholder.responsibleEmail",
                )}
                className="col-span-1"
              />
              <InputMask
                control={form.control}
                label={translate(
                  "management.clients.form.label.responsiblePhone",
                )}
                name="responsible.phone"
                type="text"
                mask="(99) 99999-9999"
                placeholder={translate(
                  "management.clients.form.placeholder.responsiblePhone",
                )}
                className="col-span-1"
              />
            </div>
          </div>
          <ModalFooter
            footer={
              <Button type="submit" variant="primary">
                {translate("management.clients.button.save")}
              </Button>
            }
            closeModal={
              <Button type="button" variant="secondary" onClick={onCancel}>
                {translate("management.clients.button.cancel")}
              </Button>
            }
          />
        </form>
      </Form>
    </LoadingBlurDiv>
  );
};
